/* eslint-disable fabric-ui/no-color-hex,fabric-ui/css-best-practices */
import React, { ReactNode } from 'react';
import {
  alpha,
  buttonBaseClasses,
  checkboxClasses,
  chipClasses,
  CssBaseline,
  drawerClasses,
  iconButtonClasses,
  linearProgressClasses,
  paginationItemClasses,
  tableCellClasses,
  tableHeadClasses,
  tableRowClasses,
} from '@mui/material';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import 'react-grid-layout/css/styles.css';
import { shadowInsetBorder, shadowPrimaryBottomMd, shadowPrimaryBottomSm } from '../utils';
import '@zs-nimbus/core/base.css';

export const getCssVarColorWithOpacity = (color: string, opacity: number, base = 'transparent'): string => {
  if (opacity === 1) return color;
  if (color.startsWith('var(')) {
    return `color-mix(in srgb, ${color} ${opacity * 100}%, ${base})`;
  }
  return alpha(color, opacity);
};

export const APPBAR_HEIGHT = 56;

const lightColors = {
  neutrals: {
    100: '#FFFFFF',
    150: '#F7F9FA',
    200: '#F2F5F7',
    300: '#E9ECF0',
    350: '#E0E3E9',
    400: '#C2C8D3',
    500: '#949EB2',
    550: '#7D8699',
    600: '#5F687A',
    650: '#535C6D',
    700: '#484F61',
    800: '#363D4E',
    850: '#1F2535',
    900: '#0D1322',
  },
  primary: {
    100: '#EEF3FD',
    150: '#DBE6FC',
    200: '#C8D9FC',
    300: '#98B9FA',
    350: '#729FF9',
    400: '#427FF8',
    500: '#256CF7',
    600: '#1F5ACE',
    650: '#1948A5',
    700: '#13367C',
    800: '#0E295D',
    850: '#0B2049',
    900: '#050E20',
  },
  positive: {
    100: '#E6F3F1',
    200: '#C3E3DD',
    300: '#A4D5CC',
    400: '#75BFB2',
    500: '#3DA592',
    600: '#2D7A6C',
    700: '#1D4E45',
    800: '#173F37',
    900: '#112E29',
  },
  green: {
    100: '#E0F7DD',
    200: '#C2E5BD',
    300: '#A9D6A2',
    400: '#85C07D',
    500: '#62AB57',
    600: '#3F7B35',
    700: '#1E4E16',
    800: '#143F0F',
    900: '#0C3107',
  },
  negative: {
    100: '#FFE6E7',
    150: '#FADFE4',
    200: '#F4AEAC',
    300: '#EA7C78',
    400: '#E04B44',
    500: '#DC362E',
    600: '#B1322A',
    700: '#8E201C',
    800: '#671B1C',
    900: '#300906',
  },
  complementary: {
    100: '#FFFDE2',
    150: '#FFF9C2',
    200: '#FFF6A3',
    300: '#FFF17D',
    350: '#FCE656',
    400: '#FCDB35',
    500: '#FACD1D',
    600: '#F1B209',
    650: '#E8A000',
    700: '#DD8400',
    800: '#D36C00',
    850: '#C85600',
    900: '#BD4200',
  },
  orange: {
    100: '#FFF2E4',
    200: '#FAD2A4',
    300: '#F7BD7A',
    400: '#F4A84F',
    500: '#F19325',
    600: '#BE731C',
    700: '#8D5513',
    800: '#55320B',
    900: '#372005',
  },
  yellow: {
    100: '#FFF8DD',
    200: '#FEEEA3',
    300: '#FDE77C',
    400: '#FBDC42',
    500: '#FAD208',
    600: '#D0AE07',
    700: '#A68906',
    800: '#685205',
    900: '#3E2E04',
  },
  blue: {
    100: '#ECF7FF',
    200: '#C1E3FB',
    300: '#97CFF7',
    400: '#7AC3F5',
    500: '#28A9F1',
    600: '#2089C4',
    700: '#145B83',
    800: '#0E415E',
    900: '#0C2F42',
  },
  pink: {
    100: '#FDEAF6',
    200: '#FBC5DD',
    300: '#F87E9C',
    400: '#B62A5B',
    500: '#8B1A4B',
    600: '#761540',
    700: '#611036',
    800: '#4B0A2B',
    900: '#360520',
  },
  cyan: {
    100: '#E2F6FA',
    200: '#CBECF3',
    300: '#97D9EA',
    400: '#6AC9E5',
    500: '#25BAE2',
    600: '#1D98B9',
    700: '#11677E',
    800: '#0C4A5B',
    900: '#093540',
  },
  purple: {
    100: '#F4E8FE',
    200: '#EDD8FD',
    300: '#D3ACF2',
    400: '#AD6FDD',
    500: '#9F46D7',
    600: '#8446AF',
    700: '#60387C',
    800: '#441460',
    900: '#2B083F',
  },
};

const gradients = {
  red: 'linear-gradient(271deg, #E04B44 18.91%, #F4AEAC 141.41%)',
  orange: 'linear-gradient(271deg, #F19325 18.91%, #FAD2A4 141.41%)',
  yellow: 'linear-gradient(271deg, #F1B209 18.91%, #FCDB35 141.41%)',
  positive: 'linear-gradient(271deg, #3DA592 18.91%, #C3E3DD 141.41%)',
  blue: 'linear-gradient(271deg, #28A9F1 18.91%, #C1E3FB 141.41%)',
  grey: 'linear-gradient(271deg, #949EB2 18.91%, #F2F5F7 141.41%)',
  primary: 'linear-gradient(271deg, #256CF7 18.91%, #C8D9FC 141.41%)',
  purple: 'linear-gradient(271deg, #948BFF 18.91%, #D4D3FF 141.41%)',
  green: 'linear-gradient(271deg, #62AB57 18.91%, #C2E5BD 141.41%)',
};

export const chartColors = [
  'var(--dataviz-color-categorical-primary-chart01)',
  'var(--dataviz-color-categorical-primary-chart02)',
  'var(--dataviz-color-categorical-primary-chart03)',
  'var(--dataviz-color-categorical-primary-chart04)',
  'var(--dataviz-color-categorical-primary-chart05)',
  'var(--dataviz-color-categorical-primary-chart06)',
  'var(--dataviz-color-categorical-primary-chart07)',
  'var(--dataviz-color-categorical-primary-chart08)',
  'var(--dataviz-color-categorical-primary-chart09)',
  'var(--dataviz-color-categorical-primary-chart10)',
  'var(--dataviz-color-categorical-primary-chart11)',
  'var(--dataviz-color-categorical-primary-chart12)',
  'var(--dataviz-color-categorical-primary-chart13)',
  'var(--dataviz-color-categorical-primary-chart14)',
  'var(--dataviz-color-categorical-primary-chart15)',
  'var(--dataviz-color-categorical-primary-chart16)',
  'var(--dataviz-color-categorical-primary-chart17)',
  'var(--dataviz-color-categorical-primary-chart18)',
  'var(--dataviz-color-categorical-primary-chart19)',
  'var(--dataviz-color-categorical-primary-chart20)',
];
const avatarColors = [
  'var(--dataviz-color-categorical-secondary-chart01)',
  'var(--dataviz-color-categorical-secondary-chart02)',
  'var(--dataviz-color-categorical-secondary-chart03)',
  'var(--dataviz-color-categorical-secondary-chart04)',
  'var(--dataviz-color-categorical-secondary-chart05)',
  'var(--dataviz-color-categorical-secondary-chart06)',
  'var(--dataviz-color-categorical-secondary-chart07)',
  'var(--dataviz-color-categorical-secondary-chart08)',
  'var(--dataviz-color-categorical-secondary-chart09)',
  'var(--dataviz-color-categorical-secondary-chart10)',
  'var(--dataviz-color-categorical-secondary-chart11)',
  'var(--dataviz-color-categorical-secondary-chart12)',
  'var(--dataviz-color-categorical-secondary-chart13)',
  'var(--dataviz-color-categorical-secondary-chart14)',
  'var(--dataviz-color-categorical-secondary-chart15)',
  'var(--dataviz-color-categorical-secondary-chart16)',
  'var(--dataviz-color-categorical-secondary-chart17)',
  'var(--dataviz-color-categorical-secondary-chart18)',
  'var(--dataviz-color-categorical-secondary-chart19)',
  'var(--dataviz-color-categorical-secondary-chart20)',
];

export const flex = {
  row: {
    display: 'flex',
  },
  wrap: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
  },
  colCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  colCenterBetween: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  colJustifyCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  colJustifyBetween: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  colJustifyAround: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
  },
  colItemsStart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  colItemsCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  colItemsEnd: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
  },
  colStartCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'center',
  },
  colStartAround: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'space-around',
  },
  colEndCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'end',
    alignItems: 'center',
  },
  colEndStart: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'end',
    alignItems: 'start',
  },
  colEnd: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'end',
    alignItems: 'end',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  reverseEnd: {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
  },
  justifyBetween: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  justifyBetweenCenter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  justifyStartEnd: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  justifyBetweenStart: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  justifyStart: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  justifyBetweenEnd: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  justifyAround: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  justifyAroundCenter: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  justifyEnd: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  justifyEndCenter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  justifyStartCenter: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  justifyCenterBaseline: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
  },
  justifyCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
  justifyCenterStart: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  itemsCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  itemsEnd: {
    display: 'flex',
    alignItems: 'end',
  },
  itemsStart: {
    display: 'flex',
    alignItems: 'start',
  },
  itemsBaseline: {
    display: 'flex',
    alignItems: 'baseline',
  },
  alignEnd: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  endStart: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'start',
  },
};
const getTheme = () => {
  const colors = lightColors;

  const defaultTheme = createTheme({
    cssVariables: {
      colorSchemeSelector: 'data-mui-color-scheme',
    },
    transitions: {
      duration: {
        shortest: 50,
        shorter: 100,
        short: 150,
        // most basic recommended timing
        standard: 300,
        // this is to be used in complex animations
        complex: 375,
        // recommended when something is entering screen
        enteringScreen: 225,
        // recommended when something is leaving screen
        leavingScreen: 195,
      },
    },
    palette: {
      colors,
      chartColors: [...chartColors, ...chartColors, ...chartColors, ...chartColors],
      avatarColors,
      background: {
        default: 'var(--color-background-primary)',
      },
      primary: {
        main: 'var(--color-surface-interactive-primary-default)',
        light: 'var(--color-surface-interactive-primary-default)',
        dark: 'var(--color-surface-interactive-primary-default)',
        contrastText: 'var(--color-content-inverted-base-primary)',
      },
      unknown: {
        main: colors.positive[500],
      },
      info: {
        main: colors.positive[500],
      },
      low: {
        main: 'var(--dataviz-color-severity-primary-low)',
        light: 'var(--dataviz-color-severity-primary-low)',
        dark: 'var(--dataviz-color-severity-primary-low)',
      },
      success: {
        main: 'var(--color-content-status-success-secondary)',
        light: 'var(--color-content-status-success-secondary)',
        dark: 'var(--color-content-status-success-secondary)',
        contrastText: 'var(--color-content-inverted-base-primary)',
      },
      warning: {
        main: 'var(--color-content-status-warning-secondary)',
        light: 'var(--color-content-status-warning-secondary)',
        dark: 'var(--color-content-status-warning-secondary)',
        contrastText: 'var(--color-content-inverted-base-primary)',
      },
      high: {
        main: 'var(--dataviz-color-severity-primary-high)',
        light: 'var(--dataviz-color-severity-primary-high)',
        dark: 'var(--dataviz-color-severity-primary-high)',
        contrastText: 'var(--color-content-inverted-base-primary)',
      },
      error: {
        main: 'var(--color-content-status-danger-secondary)',
        light: 'var(--color-content-status-danger-secondary)',
        dark: 'var(--color-content-status-danger-secondary)',
        contrastText: 'var(--color-content-inverted-base-primary)',
      },
      text: {
        primary: 'var(--color-content-base-primary)',
        secondary: 'var(--color-content-base-secondary)',
        disabled: 'var(--color-content-base-disabled)',
      },
      white: {
        main: 'var(--color-surface-base-primary)',
      },
      action: {
        disabled: 'var(--color-content-interactive-primary-disabled)',
        hover: 'var(--color-surface-list-hover)',
        focus: 'var(--color-surface-list-hover)',
      },
      gradients,
    },
    typography: {
      fontFamily: 'Inter',
      button: {
        textTransform: 'none',
      },
      body1: {
        fontSize: '14px',
      },
      h1: {
        fontSize: '48px',
        lineHeight: 1.5,
        fontWeight: 300,
      },
      h2: {
        fontSize: '32px',
        lineHeight: 1.5,
        fontWeight: 600,
      },
      h3: {
        fontSize: '24px',
        lineHeight: 1.5,
        fontWeight: 600,
        marginBottom: '24px',
      },
      h4: {
        fontSize: '24px',
        lineHeight: 1.5,
        fontWeight: 300,
      },
      h5: {
        fontSize: '18px',
        lineHeight: '22px',
        fontWeight: 600,
      },
      h6: {
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: 600,
      },
      h7: {
        fontSize: '12px',
        lineHeight: '18px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        color: 'var(--color-content-base-secondary)',
      },
      h8: {
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.7px',
        color: 'var(--color-content-base-secondary)',
      },
    },
  });

  const tableHighlightingStyle = {
    tableHead: {
      borderRight: `1px solid ${colors.primary['500']}`,
      borderLeft: `1px solid ${colors.primary['500']}`,
      borderTop: `1px solid ${colors.primary['500']}`,
    },
    tableCell: {
      borderRight: `1px solid ${colors.primary['500']}`,
      borderLeft: `1px solid ${colors.primary['500']}`,
    },
    tableCellLastRow: {
      borderBottom: `1px solid ${colors.primary['500']}`,
    },
  };

  const linkStyle = {
    color: 'var(--color-content-base-secondary)',
    backgroundColor: 'transparent',
    ':hover': {
      color: 'var(--color-content-interactive-primary-default)',
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      color: 'var(--color-content-interactive-primary-disabled)',
    },
  };
  const listBackgroundStyle = {
    backgroundColor: 'var(--color-surface-elevated-10)',
    boxShadow: shadowPrimaryBottomMd,
  };

  const scrollbarStyles = {
    '::-webkit-scrollbar': { width: '14px', height: '14px', background: 'transparent' },
    '::-webkit-scrollbar-track': { background: 'transparent', borderRadius: '10px' },
    '::-webkit-scrollbar-thumb': {
      background: 'var(--color-surface-interactive-secondary-hover)',
      borderRadius: '10px',
      border: '3px solid transparent',
      backgroundClip: 'padding-box',
    },
    '::-webkit-scrollbar-corner': { background: 'transparent' },
    '@supports (scrollbar-color: red blue)': {
      body: { scrollbarColor: 'var(--color-surface-interactive-secondary-hover) transparent', scrollbarWidth: 'thin' },
    },
  };

  return createTheme({
    ...defaultTheme,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'var(--color-background-page)',
            fontFamily: "'Inter', 'Roboto', 'sans-serif'",
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            overflow: 'hidden',
          },
          'html, body': {
            overscrollBehavior: 'none',
          },
          a: {
            textDecoration: 'none',
            color: 'inherit',
          },
          svg: {
            color: 'inherit',
            flexShrink: 0,
            height: 20,
            width: 20,
            pointerEvents: 'none',
          },
          'input:-webkit-autofill, input:-webkit-autofill:focus': {
            transition: 'background-color 600000s 0s, color 600000s 0s',
            borderRadius: 0,
          },
          '.recharts-tooltip-wrapper': {
            outline: 'none',
          },
          '.recharts-layer.recharts-pie, .recharts-sector:focus, .recharts-layer .recharts-pie-sector': {
            outline: 'none !important',
            outlineColor: 'none',
            outlineStyle: 'none',
          },
          '.custom-venn-tooltip': {
            transform: 'none !important',
          },
          ...scrollbarStyles,
          mark: {
            background: colors.complementary[150],
          },
          '.avalor-full-screen': {
            position: 'fixed !important',
            inset: '0px 0px 0px 0px !important',
            transform: `translate3d(calc(var(--side-bar-width) + 24px), ${APPBAR_HEIGHT + 24}px, 0px) !important`,
            zIndex: defaultTheme.zIndex.modal,
            width: `calc(100vw - var(--side-bar-width) - ${24 * 2}px) !important`,
            height: `calc(100vh - ${APPBAR_HEIGHT + 24 * 2}px) !important`,
          },
          fallbacks: [
            {
              '@font-face': {
                fontFamily: 'Inter',
                src: 'url("/Inter-VariableFont_slnt,wght.ttf") format("truetype-variations")',
                fontWeight: '1 999',
              },
            },
            {
              '@font-face': {
                fontFamily: 'Outfit',
                src: 'url("/Outfit-SemiBold.ttf") format("truetype")',
              },
            },
          ],
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface-interactive-secondary-default)',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            gap: 8,
            transition: defaultTheme.transitions.create('color', {
              duration: defaultTheme.transitions.duration.shorter,
            }),
            '&.MuiIconButton-root': {
              padding: 0,
              color: 'var(--color-content-base-secondary)',
              '&, &:hover': {
                backgroundColor: 'transparent',
              },
              ':before': {
                content: '""',
                position: 'absolute',
                padding: 4,
                width: '100%',
                height: '100%',
                boxSizing: 'content-box',
                borderRadius: 10,
                backgroundColor: 'transparent',
                transition: defaultTheme.transitions.create('background-color', {
                  duration: defaultTheme.transitions.duration.shorter,
                }),
              },
              '&:hover:before': {
                backgroundColor: 'var(--color-surface-list-hover)',
              },
              svg: {
                width: 18,
                height: 18,
                position: 'relative', // needed for background positioning
              },
            },
            '&.Mui-disabled img': {
              opacity: 0.4,
            },
          },
        },
      },
      MuiCalendarPicker: {
        styleOverrides: {
          root: {
            width: 266,
            height: 270,
            '.MuiPickersDay-root, .MuiTypography-root': {
              width: 26,
              height: 26,
              borderRadius: '10px',
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 8,
            paddingRight: 16,
            borderRadius: '80px',
            backgroundColor: colors.neutrals[100],
            '&, &:active, &:hover': {
              boxShadow: '0px 2px 20px 0px rgba(18, 18, 27, 0.15)',
            },
          },
        },
        variants: [
          {
            // @ts-ignore
            props: { active: true },
            style: {
              backgroundColor: colors.primary[100],
              border: `1px solid ${colors.primary[500]}`,
            },
          },
        ],
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'text' },
            style: {
              padding: 0,
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              color: 'var(--color-content-interactive-secondary-default)',
              backgroundColor: 'var(--color-surface-field-default)',
              borderColor: 'var(--color-border-severity-none-secondary)',
              '&:hover': {
                color: 'var(--color-content-interactive-secondary-hover)',
                backgroundColor: 'var(--color-surface-interactive-modifier-default)',
                borderColor: 'var(--color-border-interactive-primary-default)',
              },
              '&.Mui-disabled': {
                color: 'var(--color-content-interactive-secondary-disabled)',
                backgroundColor: 'var(--color-surface-interactive-secondary-disabled)',
                borderColor: 'var(--color-border-interactive-secondary-disabled)',
              },
            },
          },
          {
            // @ts-ignore
            props: { variant: 'outlined', active: true },
            style: {
              color: 'var(--color-content-interactive-secondary-active)',
              backgroundColor: 'var(--color-surface-interactive-modifier-hover)',
              borderColor: 'var(--color-border-interactive-primary-default)',
            },
          },
          {
            props: { variant: 'contained' },
            style: {
              backgroundColor: 'var(--color-surface-interactive-primary-default)',
              color: 'var(--color-content-inverted-base-primary)',
              '&:hover': {
                '&.MuiButton-containedPrimary': {
                  backgroundColor: 'var(--color-surface-interactive-primary-hover)',
                },
                boxShadow: `none`,
              },
              '&.Mui-disabled': {
                backgroundColor: 'var(--color-surface-interactive-primary-disabled)',
                color: 'var(--color-content-interactive-primary-disabled)',
              },
            },
          },
          {
            // @ts-ignore
            props: { variant: 'contained', active: true },
            style: {
              backgroundColor: 'var(--color-surface-interactive-primary-active)',
              color: 'var(--color-content-inverted-base-primary)',
            },
          },
          {
            // @ts-ignore
            props: { variant: 'secondaryContained' },
            style: {
              backgroundColor: 'var(--color-surface-interactive-secondary-default)',
              color: 'var(--color-content-interactive-secondary-default)',
              '&:hover': {
                backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'var(--color-surface-interactive-secondary-disabled)',
                color: 'var(--color-content-interactive-secondary-disabled)',
              },
            },
          },
          {
            props: { size: 'xSmall' },
            style: {
              gap: '4px',
              fontSize: 13,
              lineHeight: '18px',
              borderRadius: '8px',
              padding: '3px 10px',
              [`.${chipClasses.root}`]: {
                height: 18,
                [`.${chipClasses.label}`]: {
                  paddingLeft: 6,
                  paddingRight: 6,
                },
              },
              svg: {
                height: 18,
                width: 18,
              },
            },
          },
          {
            props: { size: 'small' },
            style: {
              fontSize: 14,
              lineHeight: '22px',
              borderRadius: 10,
              padding: '5px 16px',
            },
          },
          {
            props: { size: 'large' },
            style: {
              padding: '12px 24px',
            },
          },
          {
            // @ts-ignore
            props: { size: 'small', type: 'icon' },
            style: {
              padding: '6px',
            },
          },
          {
            // @ts-ignore
            props: { size: 'large', type: 'icon' },
            style: {
              padding: '8px',
            },
          },
          {
            props: { size: 'small', variant: 'outlined' },
            style: {
              padding: '4px 16px',
            },
          },
          {
            props: { size: 'xSmall', variant: 'outlined' },
            style: {
              padding: '2px 10px',
            },
          },
          {
            // @ts-ignore
            props: { size: 'xSmall', type: 'icon' },
            style: {
              padding: '3px',
              lineHeight: '18px',
              borderRadius: '8px',
            },
          },
          {
            // @ts-ignore
            props: { size: 'xSmall', type: 'icon', variant: 'outlined' },
            style: {
              padding: '2px',
            },
          },
          {
            // @ts-ignore
            props: { size: 'small', type: 'icon', variant: 'outlined' },
            style: {
              padding: '6px',
            },
          },
          {
            props: { color: 'error', variant: 'contained' },
            style: {
              backgroundColor: 'var(--color-surface-interactive-danger-default)',
              color: 'var(--color-content-inverted-base-primary)',
              '&:hover': {
                backgroundColor: 'var(--color-surface-interactive-danger-hover)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'var(--color-surface-interactive-danger-disabled)',
                color: 'var(--color-content-interactive-primary-disabled)',
              },
            },
          },
          {
            props: { color: 'warning', variant: 'contained' },
            style: {
              backgroundColor: 'var(--dataviz-color-status-primary-warning)',
              color: 'var(--color-content-inverted-base-primary)',
              '&:hover': {
                backgroundColor: 'var(--color-content-accent-orange-secondary)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'var(--color-surface-interactive-primary-disabled)',
                color: 'var(--color-content-interactive-primary-disabled)',
              },
            },
          },
          {
            props: { variant: 'text' },
            style: linkStyle,
          },
          {
            props: { variant: 'link' },
            style: { ...linkStyle, padding: 0 },
          },
          {
            // @ts-ignore
            props: { variant: 'filter', active: true },
            style: {
              color: 'var(--color-content-interactive-primary-default)',
              backgroundColor: 'var(--color-surface-interactive-modifier-active)',
              ':hover': {
                backgroundColor: 'var(--color-surface-interactive-modifier-hover)',
              },
            },
          },
          {
            props: { variant: 'primaryText' },
            style: {
              color: 'var(--color-content-interactive-primary-default)',
              padding: 0,
              backgroundColor: 'transparent',
              ':hover': {
                color: 'var(--color-content-interactive-primary-hover)',
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                color: 'var(--color-content-interactive-primary-disabled)',
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            boxShadow: 'none',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: 1.5,
            borderRadius: 12,
            padding: '8px 16px',
            minWidth: 'inherit',
          },
          // @ts-ignore
          filter: {
            paddingTop: '5px',
            paddingBottom: '5px',
            borderRadius: '10px',
            lineHeight: '21px',
            color: 'var(--color-content-interactive-secondary-default)',
            backgroundColor: 'var(--color-surface-interactive-secondary-default)',
            '&.transparent': {
              // tertiary variant
              color: 'var(--color-content-interactive-tertiary-default)',
              backgroundColor: 'transparent',
              '&:hover': {
                color: 'var(--color-content-interactive-tertiary-hover)',
                backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
              },
              '&.Mui-disabled': {
                color: 'var(--color-content-interactive-tertiary-disabled)',
              },
            },
            '&:hover': {
              color: 'var(--color-content-interactive-secondary-hover)',
              backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
            },
            '&.active-filter': {
              color: 'var(--color-content-interactive-primary-default)',
              backgroundColor: 'var(--color-surface-interactive-modifier-active)',
              ':hover': {
                backgroundColor: 'var(--color-surface-interactive-modifier-hover)',
              },
              '&.transparent': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                color: 'var(--color-content-interactive-secondary-disabled)',
                backgroundColor: 'var(--color-surface-interactive-secondary-disabled)',
              },
            },
            '&.Mui-focusVisible': {
              color: 'var(--color-content-interactive-secondary-hover)',
              backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
              '&.active-filter:not(.transparent)': {
                backgroundColor: 'var(--color-surface-interactive-modifier-hover)',
              },
            },
            [`.${chipClasses.root}`]: {
              height: 20,
              [`.${chipClasses.label}`]: {
                paddingLeft: 6,
                paddingRight: 6,
              },
            },
            '&.placeholder span': {
              color: 'var(--color-content-base-secondary)',
            },
            svg: {
              width: 16,
              height: 16,
            },
          },
        },
      },
      MuiLinearProgress: {
        variants: [
          {
            props: { color: 'secondary' },
            style: {
              [`&.${linearProgressClasses.root}`]: {
                background: colors.neutrals[300],
              },
              [`.${linearProgressClasses.barColorSecondary}`]: {
                background: colors.neutrals[400],
              },
            },
          },
          {
            props: { variant: 'determinate' },
            style: {
              '@keyframes gradient': {
                '0%': { backgroundPosition: '0 0' },
                '100%': { backgroundPosition: '-200% 0' },
              },
              backgroundColor: colors.neutrals[300],
              '& .MuiLinearProgress-bar': {
                background: `repeating-linear-gradient(to right, ${colors.primary[400]} 0%, ${colors.primary[150]} 50%, ${colors.primary[400]} 100%)`,
                backgroundSize: '200% auto',
                backgroundPosition: '0 100%',
                animation: 'gradient 2s infinite',
                animationFillMode: 'forwards',
                animationTimingFunction: 'linear',
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            backgroundColor: 'var(--dataviz-color-sequential-blue-scale02)',
            flexShrink: 0,
            height: '3px',
          },
          bar: {
            backgroundColor: 'var(--dataviz-color-sequential-blue-scale05)',
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            height: 4,
            '.MuiSlider-thumb': {
              width: 24,
              height: 24,
              backgroundColor: 'var(--color-surface-base-primary)',
              border: `2px solid var(--color-content-base-primary)`,
              transition: defaultTheme.transitions.create(['filter'], {
                duration: defaultTheme.transitions.duration.shorter,
              }),
              '&:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: 'none',
                filter:
                  'drop-shadow(var(--color-effects-position-none) var(--color-effects-position-none) var(--color-effects-blur-ambient-md) var(--color-effects-color-default)) drop-shadow(var(--color-effects-position-none) var(--color-effects-position-default-md) var(--color-effects-blur-offset-md) var(--color-effects-color-strong))',
              },
              '.MuiSlider-valueLabel': {
                color: 'var(--color-content-immutable-white)',
                backgroundColor: 'var(--color-surface-immutable-darkGrey)',
              },
            },
            '.MuiSlider-rail': {
              backgroundColor: 'var(--color-surface-base-tertiary)',
            },
            '.MuiSlider-track': {
              backgroundColor: 'var(--color-content-interactive-primary-default)',
            },
            '.MuiSlider-mark': {
              width: '2px',
              backgroundColor: 'transparent',
              pointerEvents: 'none',
            },
            '.MuiSlider-markLabel': {
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--color-content-base-secondary)',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            color: 'inherit',
            border: '1px solid transparent',
            transition: defaultTheme.transitions.create(['color', 'background-color'], {
              duration: defaultTheme.transitions.duration.shorter,
            }),
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: colors.neutrals[350],
            },
            '&.MuiToggleButton-white': {
              '&:not(.Mui-selected)': {
                color: 'var(--color-content-interactive-secondary-default)',
                backgroundColor: 'var(--color-surface-interactive-secondary-default)',
                '&:hover': {
                  color: 'var(--color-content-interactive-secondary-hover)',
                  backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
                },
              },
              '&.Mui-disabled': {
                color: 'var(--color-content-interactive-secondary-disabled)',
                backgroundColor: 'var(--color-surface-interactive-secondary-disabled)',
              },
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 10,
              padding: '3px 12px',
              '.MuiToggleButtonGroup-root &.MuiToggleButtonGroup-grouped': {
                borderColor: 'transparent',
                '&:not(.Mui-selected)': {
                  '&:not(:first-of-type)': {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                  '&:not(:last-of-type)': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                  '.Mui-selected + &': {
                    marginLeft: '-11px',
                  },
                  '&:has(+ .Mui-selected)': {
                    paddingRight: '23px',
                  },
                },
                '&.Mui-selected': {
                  '&:not(:first-of-type)': {
                    marginLeft: '-12px',
                  },
                  '&:not(:last-of-type)': {
                    marginRight: '-10px',
                  },
                  '+ :not(.Mui-selected)': {
                    paddingLeft: '22px',
                  },
                },
              },
              '&.MuiToggleButtonGroup-grouped.Mui-selected': {
                backgroundColor: 'var(--color-surface-base-primary)',
                color: 'var(--color-content-interactive-secondary-default)',
                borderColor: 'var(--color-border-base-primary)',
                borderRadius: 10,
                zIndex: 1,
                pointerEvents: 'none',
                '&.Mui-disabled': {
                  borderColor: 'var(--color-border-interactive-secondary-disabled)',
                  color: 'var(--color-content-interactive-secondary-disabled)',
                },
              },
            },
          },
          // @ts-ignore
          sizeXSmall: {
            minHeight: 24,
            borderRadius: '10px',
            padding: '3px 11px',
            fontSize: 12,
            // white variant
            '&.MuiToggleButton-white': {
              fontSize: 12,
              borderRadius: 10,
            },
            '&, .MuiToggleButtonGroup-root &.MuiToggleButtonGroup-grouped': {
              borderColor: 'transparent',
              fontWeight: 600,
              lineHeight: '1.5',
            },
            '&:not(.Mui-selected)': {
              backgroundColor: colors.neutrals[300],
              color: colors.neutrals[600],
              '&:hover': {
                backgroundColor: colors.neutrals[350],
                color: colors.neutrals[700],
              },
            },
            '&.Mui-selected': {
              backgroundColor: colors.primary[150],
              '&:hover': {
                backgroundColor: colors.primary[150],
                color: colors.neutrals[700],
              },
              color: colors.neutrals[700],
              '&.Mui-disabled': {
                color: 'var(--color-content-interactive-secondary-disabled)',
              },
            },
            svg: {
              width: 16,
              height: 16,
            },
          },
          sizeSmallOutline: { height: 32 },
          sizeSmall: {
            minHeight: 32,
            borderRadius: '10px',
            padding: '5px 11px',
            fontSize: 14,
            '&, .MuiToggleButtonGroup-root &.MuiToggleButtonGroup-grouped': {
              borderColor: 'transparent',
              fontWeight: 600,
              lineHeight: '1.5',
            },
            '&:not(.Mui-selected)': {
              backgroundColor: colors.neutrals[300],
              color: colors.neutrals[600],
              '&:hover': {
                backgroundColor: colors.neutrals[350],
                color: colors.neutrals[700],
              },
            },
            '&.Mui-selected': {
              backgroundColor: colors.primary[150],
              '&:hover': {
                backgroundColor: colors.primary[150],
                color: colors.neutrals[700],
              },
              color: colors.neutrals[800],
            },
            svg: {
              width: 18,
              height: 18,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: '2px',
            color: 'var(--color-content-base-primary)',
            svg: { color: 'var(--color-content-base-secondary)' },
            transition: defaultTheme.transitions.create(['background-color'], {
              duration: defaultTheme.transitions.duration.shorter,
            }),
            '&:hover': {
              backgroundColor: 'var(--color-surface-list-hover)',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--color-surface-list-focus)',
              '&:hover': {
                backgroundColor: 'var(--color-surface-list-hover)',
              },
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            color: 'var(--color-content-inverted-base-primary)',
            backgroundColor: 'var(--color-content-inverted-interactive-disabled)',
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 3,
        },
        styleOverrides: {
          root: listBackgroundStyle,
          rounded: {
            borderRadius: 2,
          },
        },
      },
      MuiDivider: {
        variants: [
          {
            props: { variant: 'normal' },
            style: {
              marginTop: '4px',
              marginBottom: '4px',
            },
          },
        ],
        styleOverrides: {
          root: {
            borderColor: 'var(--color-border-base-primary)',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: '8px',
            '& .MuiDivider-root': {
              margin: '8px',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },
      MuiChip: {
        variants: [
          {
            props: { size: 'medium' },
            style: {
              margin: '0',
              height: '100%',
              padding: '2px 8px 2px 10px',
              zIndex: 1,
              '& .MuiChip-label': {
                fontSize: 14,
                lineHeight: '22px',
                padding: 0,
                marginRight: 6,
              },
              '& .MuiChip-deleteIcon': {
                pointerEvents: 'inherit',
                marginRight: 0,
                '&:hover': {
                  color: colors.primary[600],
                },
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface-base-secondary)',
            border: '1px solid var(--color-border-accent-blueGrey-primary)',
            '& .MuiChip-label': {
              color: 'var(--color-content-interactive-secondary-active)',
            },
            '& .MuiChip-deleteIcon': {
              color: 'var(--color-content-base-tertiary)',
            },
          },
        },
      },
      MuiAutocomplete: {
        variants: [
          {
            props: { size: 'xSmall' },
            style: {
              '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                gap: '4px',
                minHeight: 24,
                borderRadius: '8px',
                padding: '3px 30px 3px 8px',
              },
              '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                minHeight: 24,
                borderRadius: '8px',
              },
              '& .MuiChip-root': {
                padding: '0 6px 0 10px',
                '&:has(>:first-of-type img), &:has(>:first-of-type svg)': {
                  paddingLeft: '4px',
                },
                '& img, & svg': {
                  borderRadius: '50%',
                  maxHeight: '14px',
                  maxWidth: '14px',
                },
                '& .MuiChip-label': {
                  fontSize: 12,
                  lineHeight: '16px',
                },
              },
              '& .MuiChip-deleteIcon': {
                width: '16px',
                height: '16px',
              },
              '& .MuiInputBase-inputAdornedStart': {
                fontSize: '12px',
                lineHeight: '16px',
                marginLeft: '4px',
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                width: '20px',
                height: '20px',
              },
              '& .MuiAutocomplete-endAdornment': {
                lineHeight: '16px',
              },
              '& .MuiInputAdornment-root': {
                marginLeft: '8px',
              },
            },
          },
          {
            props: { size: 'small' },
            style: {
              '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                gap: '4px',
                minHeight: 32,
                borderRadius: '10px',
                padding: '5px 30px 5px 8px',
              },
              '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                minHeight: 32,
                borderRadius: '10px',
              },
              '& .MuiChip-root': {
                padding: '0 6px 0 10px',
                '&:has(>:first-of-type img), &:has(>:first-of-type svg)': {
                  paddingLeft: '4px',
                },
                '& img, & svg': {
                  borderRadius: '50%',
                  maxHeight: '18px',
                  maxWidth: '18px',
                },
                '& .MuiChip-label': {
                  fontSize: 14,
                  lineHeight: '20px',
                },
              },
              '& .MuiChip-deleteIcon': {
                width: '20px',
                height: '20px',
              },
              '& .MuiInputBase-inputAdornedStart': {
                fontSize: '14px',
                lineHeight: '20px',
                marginLeft: '4px',
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                width: '20px',
                height: '20px',
              },
              '& .MuiAutocomplete-endAdornment': {
                lineHeight: '20px',
              },
              '& .MuiInputAdornment-root': {
                marginLeft: '8px',
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                gap: '6px',
                minHeight: 40,
                padding: '7px 30px 7px 8px',
              },
              '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                minHeight: 40,
                borderRadius: '10px',
              },
              '& .MuiChip-root': {
                '&:has(>:first-of-type img), &:has(>:first-of-type svg)': {
                  paddingLeft: '4px',
                },
                padding: '2px 6px 2px 10px',
                '& img, & svg': {
                  borderRadius: '50%',
                  maxHeight: '20px',
                  maxWidth: '20px',
                },
                '& .MuiChip-label': {
                  fontSize: 14,
                  lineHeight: '20px',
                },
              },
              '& .MuiChip-deleteIcon': {
                width: '20px',
                height: '20px',
              },
              '& .MuiInputBase-inputAdornedStart': {
                fontSize: '14px',
                lineHeight: '20px',
                marginLeft: '4px',
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                width: '20px',
                height: '20px',
              },
              '& .MuiAutocomplete-endAdornment': {
                lineHeight: '18px',
              },
              '& .MuiInputAdornment-root': {
                marginLeft: '8px',
              },
            },
          },
          {
            props: {
              readOnly: true,
            },
            style: {
              '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-border-base-primary) !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                backgroundColor: 'var(--color-surface-field-disabled)',
              },
              '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                color: 'var(--color-content-base-secondary)',
                paddingRight: '8px',
              },
              '& .MuiAutocomplete-tag.MuiChip-root': {
                pointerEvents: 'auto',
                '& .MuiChip-deleteIcon': {
                  pointerEvents: 'none',
                  color: colors.primary[200],
                },
              },
              '& .MuiAutocomplete-tag.MuiChip-root.Mui-disabled': {
                opacity: 1,
                color: 'var(--color-content-base-secondary)',
                '& .MuiChip-label': {
                  color: 'var(--color-content-base-disabled)',
                },
                '& .MuiChip-deleteIcon': {
                  color: colors.primary[200],
                },
              },
            },
          },
          {
            props: {
              disabled: true,
            },
            style: {
              '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
                backgroundColor: 'var(--color-surface-field-disabled)',
                color: 'var(--color-content-base-tertiary)',
              },
              '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                paddingRight: '8px',
              },
              '& .MuiAutocomplete-tag.MuiChip-root': {
                opacity: '1 !important',
                backgroundColor: 'var(--color-surface-interactive-secondary-disabled)',
                borderColor: 'var(--color-border-interactive-secondary-disabled)',
                color: 'var(--color-content-base-disabled)',
                '& .MuiChip-label': {
                  color: 'var(--color-content-base-disabled)',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'var(--color-content-base-disabled)',
                },
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            maxWidth: 300,
            width: '100%',
            '&.autocomplete-multiple': {
              '&, & .MuiFormControl-root': {
                maxWidth: 450,
              },
            },
            '& .MuiAutocomplete-tag': {
              margin: 0,
            },
            '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
              '& .MuiAutocomplete-input': {
                padding: 0,
                '& + .MuiAutocomplete-endAdornment': {
                  right: '10px',
                  [`.${iconButtonClasses.root}`]: {
                    margin: '0 2px',
                  },
                },
              },
            },
            '&.MuiAutocomplete-hasClearIcon.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
              paddingRight: '49px',
            },
            '& .MuiAutocomplete-tag.MuiChip-root': {
              cursor: 'default',
            },
            '.MuiInputAdornment-root .notranslate': {
              display: 'none',
            },
          },
          paper: {
            padding: '8px',
            boxShadow: listBackgroundStyle.boxShadow,
          },
          listbox: {
            padding: 0,
            '.MuiAutocomplete-option': {
              paddingLeft: '8px',
              '&[aria-selected="true"]': {
                '&.has-checkbox': {
                  backgroundColor: 'transparent',
                },
                '&, &.Mui-focused': {
                  backgroundColor: 'var(--color-surface-list-focus)',
                },
              },
            },
          },
          option: {
            gap: '8px',
          },
          groupLabel: {
            ...defaultTheme.typography.h7,
            lineHeight: '40px',
            padding: '0 8px',
            backgroundColor: listBackgroundStyle.backgroundColor,
          },
          clearIndicator: {
            padding: 2,
          },
          groupUl: {
            '.MuiAutocomplete-option': {
              paddingLeft: '16px',
            },
          },
        },
      },
      MuiTextField: {
        variants: [
          {
            props: { size: 'xSmall' },
            style: {
              '& .MuiFormLabel-root.MuiInputLabel-root': {
                marginBottom: '3px',
                fontSize: 12,
              },
              '& .MuiInputBase-root': {
                fontSize: 12,
                padding: '0px 10px',
                '&, & .MuiOutlinedInput-notchedOutline': {
                  minHeight: 24,
                  borderRadius: '8px',
                },
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            maxWidth: 300,
            width: '100%',
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            transform: 'none',
            position: 'initial',
            fontSize: '13px',
            lineHeight: 1.5,
            marginBottom: '4px',
            color: 'var(--color-content-base-secondary)',
            '&.Mui-disabled': {
              color: 'var(--color-content-base-tertiary)',
            },
            '&:not(.Mui-disabled) .MuiInputLabel-asterisk': {
              color: 'var(--color-content-status-danger-secondary)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        variants: [
          {
            // @ts-ignore
            props: { size: 'xSmall' },
            style: {
              '&.MuiInputBase-multiline': {
                padding: '0px 10px',
                '&, & .MuiOutlinedInput-notchedOutline': {
                  minHeight: 24,
                  borderRadius: '8px',
                },
              },
            },
          },
          {
            props: { disabled: true },
            style: {
              backgroundColor: 'var(--color-surface-field-disabled)',
              '& .MuiButtonBase-root': {
                pointerEvents: 'none',
              },
              '.MuiOutlinedInput-notchedOutline': {
                backgroundColor: 'var(--color-surface-field-disabled)',
                border: '1px solid var(--color-border-base-primary)',
                '&:hover': {
                  border: '1px solid var(--color-border-base-primary)',
                },
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            lineHeight: 1.5,
            width: '100%',
            borderRadius: 12,
            backgroundColor: 'var(--color-surface-field-default)',
            color: 'var(--color-content-base-primary)',
            transition: defaultTheme.transitions.create(['background-color'], {
              duration: defaultTheme.transitions.duration.shorter,
            }),
            fontWeight: 400,
            fontSize: 14,
            padding: '7px 15px',
            '&.MuiInputBase-sizeSmall': {
              padding: '3px 15px',
              '&, & .MuiOutlinedInput-notchedOutline': {
                borderRadius: 10,
                minHeight: '32px',
              },
            },
            '&.MuiInputBase-sizeLarge': {
              // sizeLarge doesn't exist
              fontSize: 16,
              minHeight: '48px',
              padding: '12px 15px',
            },
            '&.MuiInputBase-multiline': {
              padding: '9.5px 15px',
              '&.MuiInputBase-sizeSmall': {
                minHeight: '32px',
                padding: '3px 15px',
              },
            },
            '&.MuiInputBase-root': {
              gap: 8,
            },
            '& .MuiInputBase-input': {
              zIndex: 1,
              padding: 0,
              textOverflow: 'ellipsis',
              '&.Mui-disabled': {
                color: 'var(--color-content-base-tertiary)',
                textFillColor: 'inherit',
              },
              '&::placeholder': {
                color: 'var(--color-content-base-tertiary)',
                opacity: 1,
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--color-border-interactive-secondary-default)',
              backgroundColor: 'transparent',
              width: '100%',
              transition: defaultTheme.transitions.create(['border-color', 'background-color']),
              top: 0,
              '& legend': {
                height: 0,
                width: 0,
              },
              borderRadius: 12,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-border-interactive-secondary-disabled)',
            },
            '& svg': {
              width: 18,
              height: 18,
            },
            '&.MuiInputBase-sizeLarge .MuiOutlinedInput-notchedOutline': {
              // sizeLarge doesn't exist
              minHeight: '48px',
            },
            '&:not(.transparent):not(.Mui-focused):not(.Mui-error):not(.Mui-disabled):hover': {
              backgroundColor: 'var(--color-surface-field-hover)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--color-border-base-secondary)',
              },
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: 'var(--color-border-interactive-primary-default)',
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            '&.Mui-disabled': {
              color: 'var(--color-content-interactive-primary-disabled)',
            },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            padding: 0,
            borderRadius: '4px',
            color: 'var(--color-content-base-tertiary)',
            transition: defaultTheme.transitions.create(['color', 'background-color'], {
              duration: defaultTheme.transitions.duration.shorter,
            }),
            '&.Mui-disabled': {
              backgroundColor: 'var(--color-surface-interactive-secondary-disabled)',
              color: 'var(--color-border-interactive-primary-disabled)',
            },
            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
              color: 'var(--color-surface-interactive-primary-default)',
              '&.Mui-disabled': {
                color: 'var(--color-border-interactive-primary-disabled)',
              },
            },
            '&:hover, &.Mui-focusVisible': {
              color: 'var(--color-surface-interactive-primary-hover)',
              backgroundColor: 'transparent',
            },
            '& svg': {
              width: 16,
              height: 16,
              'path + path': {
                // hack for hardcoded second color of the check
                fill: 'var(--color-content-inverted-base-primary) !important',
              },
            },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            width: 10,
            height: 10,
            minWidth: 10,
            padding: 5,
            right: 4,
            top: 4,
            color: colors.primary[500],
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: defaultTheme.transitions.create(['background-color'], {
              duration: defaultTheme.transitions.duration.short,
            }),
            '&.MuiTableRow-hover:not(:hover) > .MuiTableCell-root.editable:not(.edit-mode) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--color-surface-table-row-active)',
              '&.MuiTableRow-hover:hover': {
                backgroundColor: 'var(--color-surface-table-row-hover)',
              },
            },
            '&.MuiTableRow-hover:hover': {
              backgroundColor: 'var(--color-surface-table-row-hover)',
            },
            '&.active-row': {
              outline: '2px solid var(--color-border-interactive-primary-default)',
              outlineOffset: '-2px',
              borderRadius: '4px',
            },
            cursor: 'default',
            '&.editable-row': {
              cursor: 'pointer',
            },
            '& a': {
              transition: defaultTheme.transitions.create(['color'], {
                duration: defaultTheme.transitions.duration.shortest,
              }),
            },
            '&:not(.expanded-row-content):hover > .MuiTableCell-root:not(:has(svg)) a': {
              color: 'var(--color-content-interactive-primary-default)',
              textDecoration: 'underline',
            },
            '&:last-of-type > .MuiTableCell-root': {
              borderBottomColor: 'var(--color-border-base-secondary)',
              [`&:not(.${tableCellClasses.head}).active-column`]: tableHighlightingStyle.tableCellLastRow,
            },
            '&.has-expandedRow .MuiTableCell-root': {
              cursor: 'pointer',
              borderBottom: 'none',
            },
            '&:has(.Mui-focusVisible)': {
              backgroundColor: 'var(--color-surface-table-row-hover)',
            },
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            paddingTop: '8px',
            paddingBottom: '8px',
            paddingRight: '8px',
            [`.${buttonBaseClasses.root}.${paginationItemClasses.root}`]: {
              fontSize: 12,
              height: 24,
              minWidth: 24,
              margin: '0px 1px',
              color: 'var(--color-content-base-secondary)',
              borderRadius: 10,
              [`&.${paginationItemClasses.selected}`]: {
                fontWeight: 600,
                color: 'var(--color-content-base-primary)',
                backgroundColor: 'transparent',
              },
              '&:hover': {
                backgroundColor: 'var(--color-surface-interactive-secondary-default)',
              },
            },
          },
        },
      },
      MuiTable: {
        variants: [
          {
            props: { size: 'xSmall' },
            style: {
              'thead tr:first-of-type .MuiTableCell-head': {
                ...defaultTheme.typography.h7,
                color: 'var(--color-content-base-primary)',
              },
              '.MuiTableCell-head': {
                lineHeight: '1.25rem',
              },
              '.MuiTableCell-root': {
                height: 32,
                fontSize: 12,
                padding: '5px 12px',
                whiteSpace: 'nowrap',
                '&:not(.MuiTableCell-head):not(.totals-row)': {
                  borderBottomColor: 'var(--color-border-base-subdued)',
                },
                svg: {
                  height: 18,
                  width: 18,
                },
                [`.${checkboxClasses.root} svg`]: {
                  width: '16px',
                  height: '16px',
                },
              },
              '.MuiTableRow-root:has(+ .MuiTableRow-root > .MuiTableCell-root.totals-row) .MuiTableCell-root': {
                borderBottom: 'none',
              },
              '.MuiTableRow-root:has(> .MuiTableCell-root.totals-row) + .MuiTableRow-root .MuiTableCell-root:first-of-type': {
                borderBottom: 'none',
              },
              '.MuiTableRow-root:has(> .MuiTableCell-root.totals-row) + .MuiTableRow-root .MuiTableCell-root': {
                borderTop: 'none',
              },
            },
          },
          {
            props: { size: 'tiles' },
            style: {
              borderSpacing: '0px 8px',
              marginTop: '-8px',
              opacity: 1,
              transition: defaultTheme.transitions.create(['opacity', 'filter'], {
                duration: defaultTheme.transitions.duration.shorter,
              }),
              [`.${tableHeadClasses.root} .${tableCellClasses.root}`]: {
                padding: '10px 16px',
                background: 'var(--color-background-page)',
                [`.${drawerClasses.paper} &`]: {
                  backgroundColor: 'var(--color-surface-elevated-10)',
                },
                [`&.${tableCellClasses.paddingCheckbox}`]: {
                  paddingLeft: '16px',
                },
              },
              [`.${tableCellClasses.root}`]: {
                borderBottom: 'none',
              },
              tbody: {
                [`.${tableRowClasses.root}`]: {
                  [`.${tableCellClasses.root}:not(:nth-of-type(1))`]: {
                    padding: '12px 16px',
                  },
                  [`&:not(.${tableRowClasses.selected})`]: {
                    background: 'var(--color-surface-table-row-default)',
                  },
                  [`.${tableCellClasses.paddingCheckbox}`]: {
                    paddingLeft: '16px',
                  },
                },
                [`.${tableRowClasses.hover}:hover`]: {
                  background: 'var(--color-surface-table-row-hover)',
                  boxShadow: shadowPrimaryBottomSm,
                  transform: 'scale(1)',
                },
                [`.${tableRowClasses.root}:not(last-of-type) .${tableCellClasses.root}`]: {
                  verticalAlign: 'middle',
                },
              },
            },
          },
          {
            props: { size: 'small' },
            style: {
              '.MuiTableCell-root': {
                borderBottomColor: 'var(--color-border-base-subdued)',
                lineHeight: 1.5,
                '&.MuiTableCell-sizeSmall': {
                  height: '48px',
                },
                '&:not(.MuiTableCell-paddingNone)': {
                  padding: '12px 24px',
                },
                '&.editable': {
                  padding: '7px 24px',
                  '&:not(.edit-mode)': {
                    '& .MuiOutlinedInput-input, & .MuiOutlinedInput-root': {
                      cursor: 'pointer',
                    },
                    '& .MuiButtonBase-root': {
                      color: 'transparent',
                      pointerEvents: 'none',
                    },
                    '&.marked .MuiOutlinedInput-root': {
                      color: 'var(--color-content-interactive-primary-default)',
                    },
                  },
                  '&:not(.MuiTableCell-paddingCheckbox)': {
                    paddingLeft: '8px',
                  },
                },
                '&.MuiTableCell-paddingCheckbox': {
                  padding: '16px 0 16px 32px',
                  '.MuiFormControlLabel-root': {
                    ...flex.row,
                  },
                },
                '& .MuiTextField-root': {
                  maxWidth: 'inherit',
                },
                '&.MuiTableCell-head:not(.MuiTableCell-paddingCheckbox):not(.MuiTableCell-paddingNone)': {
                  color: 'var(--color-content-base-secondary)',
                  fontWeight: 600,
                  padding: '10px 24px',
                  '& .MuiTableSortLabel-root': {
                    gap: '0px',
                    whiteSpace: 'nowrap',
                    '& .MuiTableSortLabel-icon': {
                      height: '20px',
                      flexShrink: 0,
                    },
                  },
                },
                '.MuiTableRow-root': {
                  '.MuiTableCell-root:not(.MuiTableCell-paddingNone)': {
                    padding: '6px 24px',
                    height: '34px',
                  },
                },
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              '.MuiTableCell-head': {
                ...defaultTheme.typography.h7,
                color: 'var(--color-content-base-primary)',
              },
              '.MuiTableCell-root': {
                height: 40,
                fontSize: 12,
                padding: '6px 12px',
                whiteSpace: 'nowrap',
                '&:not(.MuiTableCell-head)': {
                  borderBottomColor: 'var(--color-border-base-subdued)',
                },
                svg: {
                  height: 18,
                  width: 18,
                },
              },
            },
          },
          {
            props: { size: 'large' },
            style: {
              [`.${tableHeadClasses.root} .${tableCellClasses.root}`]: {
                padding: '10px 16px',
              },
              [`.${tableCellClasses.root}`]: {
                padding: '32px 16px',
              },
            },
          },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            '--mui-palette-TableCell-border': 'var(--color-border-base-secondary)',
            '&.active-column': tableHighlightingStyle.tableCell,
            [`&.${tableCellClasses.paddingCheckbox}`]: {
              [`.${checkboxClasses.root}:before`]: {
                content: "''",
                position: 'absolute',
                width: 36,
                height: 36,
                backgroundColor: 'transparent',
                borderRadius: 8,
                transition: defaultTheme.transitions.create(['background-color'], {
                  duration: defaultTheme.transitions.duration.shorter,
                }),
                pointerEvents: 'none',
              },
              [`&:hover .${checkboxClasses.root}, .${checkboxClasses.root}:hover, .${checkboxClasses.root}.Mui-focusVisible`]: {
                [`&:not(.Mui-disabled):not(.Mui-checked):not(.${checkboxClasses.indeterminate}):before`]: {
                  backgroundColor: 'var(--color-surface-alpha-hover)',
                },
              },
              svg: {
                zIndex: 1,
              },
            },
            '.inline-actions-cell': {
              backgroundImage: 'linear-gradient(to right, transparent, var(--color-surface-table-row-hover) 20%)',
            },
          },
          head: {
            '&.active-column': tableHighlightingStyle.tableHead,
            '&:first-of-type': {
              zIndex: 4,
            },
            zIndex: 3,
            '.MuiTableCell-root &': {
              zIndex: 2,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: '24px',
            boxShadow: shadowInsetBorder,
            backgroundColor: 'var(--color-surface-base-primary)',
          },
        },
      },
      MuiTreeView: {
        styleOverrides: {
          root: {
            flexGrow: 1,
            '.MuiTreeItem-root + .MuiTreeItem-root': {
              marginTop: '4px',
            },
            '.MuiTreeItem-content': {
              padding: 0,
              transition: defaultTheme.transitions.create(['color'], {
                duration: defaultTheme.transitions.duration.shortest,
              }),
              color: colors.neutrals[800],
              '&:not(.Mui-expanded)': {
                color: colors.neutrals[600],
              },
              '&:hover, &.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '&:hover': {
                color: colors.primary[500],
              },
              '&.Mui-selected': {
                backgroundColor: colors.primary[150],
              },
              '+ .MuiTreeItem-group': {
                margin: '8px 0',
              },
            },
            '.MuiTreeItem-label': {
              padding: '4px 8px',
            },
            '.MuiTreeItem-iconContainer:not(:empty) + .MuiTreeItem-label': {
              fontWeight: 600,
            },
            svg: {
              width: 18,
              height: 18,
              transition: defaultTheme.transitions.create(['transform']),
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            color: 'var(--color-content-base-primary)',
            borderRadius: 8,
            '&.MuiPaper-root': {
              boxShadow: 'none',
            },
            '&.grey-small': {
              backgroundColor: colors.neutrals[300],
              color: colors.neutrals[600],
              fontSize: 13,
              fontWeight: 500,
              paddingRight: '11px',
              paddingLeft: '6px',
              paddingTop: '1px',
              paddingBottom: '1px',
              '> div': { padding: 0 },
              '.MuiAlert-icon': {
                marginRight: 6,
              },
            },
          },
          message: {
            lineHeight: 1.5,
          },
          icon: {
            opacity: 1,
          },
          standardInfo: {
            backgroundColor: 'var(--color-surface-status-neutral-primary)',
            border: '1px solid var(--color-border-status-neutral-default)',
            '.MuiAlert-icon': {
              color: 'var(--color-content-status-neutral-default)',
            },
          },
          standardWarning: {
            backgroundColor: 'var(--color-surface-status-warning-primary)',
            border: '1px solid var(--color-border-status-warning-primary)',
            '.MuiAlert-icon': {
              color: 'var(--color-content-status-warning-default)',
            },
          },
          standardError: {
            backgroundColor: 'var(--color-surface-status-danger-primary)',
            border: '1px solid var(--color-border-status-danger-primary)',
            '.MuiAlert-icon': {
              color: 'var(--color-content-status-danger-default)',
            },
          },
          standardSuccess: {
            backgroundColor: 'var(--color-surface-status-success-primary)',
            border: '1px solid var(--color-border-status-success-primary)',
            '.MuiAlert-icon': {
              color: 'var(--color-content-status-success-default)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 12,
            fontWeight: 400,
            padding: '3px 10px',
            color: 'var(--color-content-immutable-white)',
            backgroundColor: 'var(--color-surface-immutable-darkGrey)',
            boxShadow: shadowPrimaryBottomSm,
            borderRadius: 12,
          },
          arrow: {
            color: 'var(--color-surface-immutable-darkGrey)',
            '&:before': {
              borderTopLeftRadius: 2,
            },
          },
          popper: {
            positionVisibility: 'no-overflow',
            '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip.MuiTooltip-tooltipArrow': {
              marginTop: 6,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          flexContainer: {
            borderBottom: '1px solid',
            borderBottomColor: 'var(--color-border-base-primary)',
          },
          indicator: {
            height: '3px',
            backgroundColor: 'var(--color-content-interactive-secondary-default)',
            borderRadius: '14px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            minWidth: 'unset',
            padding: '12px 20px',
            color: 'var(--color-content-interactive-secondary-subdued)',
            transition: defaultTheme.transitions.create('color', {
              duration: defaultTheme.transitions.duration.shorter,
            }),
            '&:hover': {
              color: 'var(--color-content-interactive-secondary-hover)',
            },
            '&.Mui-selected': {
              color: 'var(--color-content-interactive-secondary-default)',
            },
            '&.Mui-disabled': {
              color: 'var(--color-content-interactive-secondary-disabled)',
            },
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            '.MuiButtonGroup-groupedOutlinedPrimary': {
              '&:hover + .MuiButtonGroup-groupedOutlined': {
                borderLeftColor: colors.primary[500],
              },
              '&.active-button': {
                color: colors.neutrals[800],
                borderColor: colors.primary[500],
                backgroundColor: colors.primary[150],
                '& + .MuiButtonGroup-groupedOutlined': {
                  borderLeftColor: colors.primary[500],
                },
              },
            },
            '.MuiButtonGroup-groupedContainedPrimary.active-button': {
              backgroundColor: 'var(--color-surface-interactive-primary-active)',
            },
            '.MuiButtonGroup-groupedContainedSecondary.active-button': {
              backgroundColor: 'var(--color-surface-interactive-secondary-active)',
            },
            '.MuiButtonGroup-groupedContainedPrimary, .MuiButtonGroup-groupedContainedSecondary': {
              '&:not(:last-of-type)': {
                borderColor: 'var(--color-border-base-subdued)',
              },
              '&.Mui-disabled': {
                borderColor: 'var(--color-border-base-subdued)',
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            gap: 16,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '18px',
            lineHeight: 1.5,
            padding: '32px 64px 0 32px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            minHeight: 48,
            padding: '0 32px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${colors.neutrals[350]}`,
            gap: 1,
            padding: '16px 24px 16px',
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          root: {
            '& .MuiStepLabel-label': {
              color: 'var(--color-content-base-primary)',
              '&.Mui-disabled': {
                color: 'var(--color-content-base-primary)',
              },
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: 'var(--color-content-status-neutral-secondary)',
            '&.Mui-active': {
              color: 'var(--color-content-status-info-secondary)',
            },
            '&.Mui-completed': {
              color: 'var(--color-content-status-info-secondary)',
            },
            '&.Mui-disabled': {
              color: 'var(--dataviz-color-utility-neutral-others)',
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: 'var(--color-border-interactive-secondary-default)',
          },
        },
      },
    },
    breakpoints: {
      values: {
        ...defaultTheme.breakpoints.values,
        desktop: 1920,
        laptopBig: 1600,
        laptop: 1440,
      },
    },
  });
};

interface FabricThemeProviderProps {
  children: ReactNode;
}

export const FabricThemeProvider = ({ children }: FabricThemeProviderProps) => {
  const theme = React.useMemo(() => getTheme(), []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
